/**
 * DocMapper — Maps documentation files to the code they describe.
 *
 * Responsibilities:
 *   1. Discover all markdown/doc files in the project
 *   2. Parse each doc to extract references to code (file paths, exports, routes)
 *   3. Build a mapping: doc file → code sections it covers
 *   4. Detect staleness: which docs reference code that has changed
 *   5. Detect orphan docs and undocumented code
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import { glob } from 'fast-glob';
import type { CodebaseSnapshot, ModuleInfo, RouteEntry } from './CodebaseAnalyzer.js';

// ─── Public types ────────────────────────────────────────────────────────────

export interface DocFile {
  /** Relative path from project root */
  file: string;
  /** Raw content */
  content: string;
  /** Parsed title (first # heading) */
  title: string;
  /** Which code files this doc references */
  referencedFiles: string[];
  /** Which exports this doc mentions */
  referencedExports: string[];
  /** Which routes this doc mentions */
  referencedRoutes: string[];
  /** Which env vars this doc mentions */
  referencedEnvVars: string[];
  /** Managed sections found (<!-- docguard:begin:ID --> ... <!-- docguard:end:ID -->) */
  managedSections: ManagedSection[];
  /** Word count */
  wordCount: number;
  /** Last modified time (epoch ms) */
  lastModified: number;
}

export interface ManagedSection {
  id: string;
  startLine: number;
  endLine: number;
  content: string;
}

export interface DocMapping {
  /** All discovered doc files */
  docs: DocFile[];
  /** Code files that are referenced by at least one doc */
  documentedFiles: Set<string>;
  /** Code files with exports but no doc references */
  undocumentedFiles: string[];
  /** Doc files that reference code files which no longer exist */
  staleDocs: StaleDoc[];
  /** Doc files not linked from any README or index */
  orphanDocs: string[];
  /** Docs grouped by the directory they live in */
  docsByDir: Record<string, DocFile[]>;
  /** Stats */
  stats: {
    totalDocs: number;
    totalManagedSections: number;
    staleCount: number;
    orphanCount: number;
    undocumentedCount: number;
    coveragePercent: number;
  };
}

export interface StaleDoc {
  docFile: string;
  reason: string;
  /** The code files that changed or were removed */
  affectedCodeFiles: string[];
}

// ─── Managed section markers ─────────────────────────────────────────────────

const MANAGED_BEGIN = /<!--\s*docguard:begin:(\S+)\s*-->/;
const MANAGED_END = /<!--\s*docguard:end:(\S+)\s*-->/;

// ─── Mapper ──────────────────────────────────────────────────────────────────

export class DocMapper {
  private rootDir: string;
  private ignorePatterns: string[];

  constructor(rootDir: string, ignorePatterns: string[] = []) {
    this.rootDir = rootDir;
    this.ignorePatterns = [
      '**/node_modules/**', '**/.git/**', '**/dist/**', '**/build/**',
      ...ignorePatterns,
    ];
  }

  /**
   * Build a complete mapping between docs and code.
   */
  async map(snapshot: CodebaseSnapshot): Promise<DocMapping> {
    const docFiles = await this.discoverDocs();
    const docs: DocFile[] = [];

    for (const absPath of docFiles) {
      const doc = await this.parseDoc(absPath, snapshot);
      if (doc) docs.push(doc);
    }

    const documentedFiles = new Set<string>();
    for (const doc of docs) {
      doc.referencedFiles.forEach(f => documentedFiles.add(f));
    }

    // Code files with exports that no doc references
    const undocumentedFiles = snapshot.modules
      .filter(m => m.exports.length > 0 && !documentedFiles.has(m.file))
      .map(m => m.file);

    // Stale docs: reference files that no longer exist in snapshot
    const codeFileSet = new Set(snapshot.modules.map(m => m.file));
    const staleDocs: StaleDoc[] = [];
    for (const doc of docs) {
      const missing = doc.referencedFiles.filter(f => !codeFileSet.has(f));
      if (missing.length > 0) {
        staleDocs.push({
          docFile: doc.file,
          reason: `References ${missing.length} file(s) that no longer exist`,
          affectedCodeFiles: missing,
        });
      }
    }

    // Orphan docs: not linked from any README or index doc
    const allDocPaths = new Set(docs.map(d => d.file));
    const linkedDocs = new Set<string>();
    for (const doc of docs) {
      // Find markdown links to other docs
      const linkPattern = /\[.*?\]\(([^)]+\.md)\)/g;
      let m: RegExpExecArray | null;
      while ((m = linkPattern.exec(doc.content)) !== null) {
        const target = this.resolveDocLink(doc.file, m[1]);
        if (target) linkedDocs.add(target);
      }
    }
    // READMEs and root docs are never orphans
    const orphanDocs = docs
      .filter(d => {
        const base = path.basename(d.file).toLowerCase();
        if (base === 'readme.md' || base === 'changelog.md' || base === 'contributing.md') return false;
        return !linkedDocs.has(d.file);
      })
      .map(d => d.file);

    // Group by directory
    const docsByDir: Record<string, DocFile[]> = {};
    for (const doc of docs) {
      const dir = path.dirname(doc.file) || '.';
      if (!docsByDir[dir]) docsByDir[dir] = [];
      docsByDir[dir].push(doc);
    }

    const totalManagedSections = docs.reduce((s, d) => s + d.managedSections.length, 0);
    const totalCodeFiles = snapshot.modules.filter(m => m.exports.length > 0).length;
    const coveragePercent = totalCodeFiles > 0
      ? Math.round((documentedFiles.size / totalCodeFiles) * 100)
      : 100;

    return {
      docs,
      documentedFiles,
      undocumentedFiles,
      staleDocs,
      orphanDocs,
      docsByDir,
      stats: {
        totalDocs: docs.length,
        totalManagedSections,
        staleCount: staleDocs.length,
        orphanCount: orphanDocs.length,
        undocumentedCount: undocumentedFiles.length,
        coveragePercent,
      },
    };
  }

  // ─── Private ─────────────────────────────────────────────────────────────

  private async discoverDocs(): Promise<string[]> {
    return glob(['**/*.md'], {
      cwd: this.rootDir,
      ignore: this.ignorePatterns,
      absolute: true,
    });
  }

  private async parseDoc(absPath: string, snapshot: CodebaseSnapshot): Promise<DocFile | null> {
    try {
      const content = await fs.readFile(absPath, 'utf-8');
      const relPath = path.relative(this.rootDir, absPath).replace(/\\/g, '/');
      const stat = await fs.stat(absPath);
      const lines = content.split('\n');

      // Title
      const titleMatch = content.match(/^#\s+(.+)/m);
      const title = titleMatch ? titleMatch[1].trim() : path.basename(relPath, '.md');

      // Extract references
      const referencedFiles = this.findFileReferences(content, snapshot);
      const referencedExports = this.findExportReferences(content, snapshot);
      const referencedRoutes = this.findRouteReferences(content, snapshot);
      const referencedEnvVars = this.findEnvVarReferences(content, snapshot);

      // Managed sections
      const managedSections = this.parseManagedSections(lines);

      return {
        file: relPath,
        content,
        title,
        referencedFiles,
        referencedExports,
        referencedRoutes,
        referencedEnvVars,
        managedSections,
        wordCount: content.split(/\s+/).length,
        lastModified: stat.mtimeMs,
      };
    } catch { return null; }
  }

  private findFileReferences(content: string, snapshot: CodebaseSnapshot): string[] {
    const found = new Set<string>();
    const codeFiles = snapshot.modules.map(m => m.file);

    for (const file of codeFiles) {
      // Match full path or just filename
      const basename = path.basename(file);
      if (content.includes(file) || content.includes(basename)) {
        found.add(file);
      }
    }
    return [...found];
  }

  private findExportReferences(content: string, snapshot: CodebaseSnapshot): string[] {
    const found = new Set<string>();
    for (const mod of snapshot.modules) {
      for (const exp of mod.exports) {
        // Only match identifiers that look like they're referencing code (backticked or in code blocks)
        const pattern = new RegExp(`\`${exp.name}\`|\\b${exp.name}\\(`, 'g');
        if (pattern.test(content)) {
          found.add(`${mod.file}:${exp.name}`);
        }
      }
    }
    return [...found];
  }

  private findRouteReferences(content: string, snapshot: CodebaseSnapshot): string[] {
    const found: string[] = [];
    for (const route of snapshot.routes) {
      if (content.includes(route.path)) {
        found.push(`${route.method} ${route.path}`);
      }
    }
    return found;
  }

  private findEnvVarReferences(content: string, snapshot: CodebaseSnapshot): string[] {
    return snapshot.envVars.filter(v => content.includes(v));
  }

  private parseManagedSections(lines: string[]): ManagedSection[] {
    const sections: ManagedSection[] = [];
    let currentId: string | null = null;
    let startLine = 0;
    const buf: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const beginMatch = lines[i].match(MANAGED_BEGIN);
      const endMatch = lines[i].match(MANAGED_END);

      if (beginMatch && !currentId) {
        currentId = beginMatch[1];
        startLine = i;
        buf.length = 0;
      } else if (endMatch && currentId && endMatch[1] === currentId) {
        sections.push({
          id: currentId,
          startLine,
          endLine: i,
          content: buf.join('\n'),
        });
        currentId = null;
      } else if (currentId) {
        buf.push(lines[i]);
      }
    }
    return sections;
  }

  private resolveDocLink(fromDoc: string, linkTarget: string): string | null {
    if (linkTarget.startsWith('http')) return null;
    const dir = path.dirname(fromDoc);
    return path.posix.normalize(path.posix.join(dir, linkTarget));
  }
}
